<template>
  <div class="relative w-full" :class="[$attrs.class]">
    <div>
      <!-- viewer header -->
      <div class="relative flex w-full items-center justify-between font-bold">
        <!-- selector -->
        <div class="flex h-full cursor-pointer">
          <SummaryViewerTab
            :is-active="summaryFormat === 'table'"
            text="Table"
            @click="handleFormatChange('table')"
          />
          <SummaryViewerTab
            v-if="showIndex"
            :is-active="summaryFormat === 'index'"
            text="Index"
            @click="handleFormatChange('index')"
          />

          <SwipeIndicator
            v-if="summaryFormat === 'table'"
            class="absolute left-1/2 top-[104px] z-[1] ml-3 -translate-x-1/2 md:flex lg:top-[108px] xl:hidden"
          />
        </div>
        <div class="hidden justify-end pb-2 md:flex">
          <SummaryDownloadButton :methodology-file-info="methodologyFileInfo" />
        </div>
      </div>

      <!-- viewer body -->
      <div class="rounded-b-xl bg-white shadow-pm">
        <transition
          enter-active-class="transition duration-300"
          leave-active-class="transition duration-300"
          enter-class="opacity-0"
          leave-to-class="opacity-0"
          mode="out-in"
        >
          <div
            v-if="summaryFormat === 'table'"
            class="relative overflow-x-auto"
          >
            <div
              class="min-w-[820px] lg:min-w-[400px] lg:max-w-2xl xl:min-w-[1000px] xl:max-w-none"
            >
              <SummaryTableNewPortal
                class="w-auto lg:w-[1000px] xl:w-auto"
                :price-summary="priceSummary"
                :category="category"
                :subscription="subscription"
              />
            </div>
          </div>
          <div v-else class="overflow-x-auto">
            <SummaryChart
              :summary="detailedData"
              :table-summary="priceIndex.summary"
              :slug="slug"
              :title="title"
              :alt-title="altTitle"
            />
          </div>
        </transition>
      </div>
    </div>

    <div class="flex pt-6 md:hidden">
      <SummaryDownloadButton :methodology-file-info="methodologyFileInfo" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SummaryChart from '~/components/price-assessments/SummaryChart.vue'
import SummaryTableNewPortal from '~/components/price-assessments/SummaryTableNewPortal.vue'
import SummaryViewerTab from '~/components/price-assessments/SummaryViewerTab.vue'
import SummaryDownloadButton from '~/components/price-assessments/SummaryDownloadButton.vue'
import SwipeIndicator from '~/components/price-assessments/SwipeIndicator.vue'
import type { TDownloadFile } from '~/types/benchmark-app.type'
import type { TGeneral } from '~/src/types/common'

type TPriceData = {
  value: number
  date: string
}

type TPriceIndex = {
  detailed: Array<{
    product: string
    data: Array<TPriceData>
  }>
  summary: TPriceData[]
}

type TPriceSummaryItem = {
  label: string
  values: Array<{ value: number; date: string }>
  frequency: string
  productName?: string
  classTextColor?: string
  authorized?: boolean
  subscription?: string
  isSustainable?: boolean
  isPublic?: boolean
  latestChange?: number
  yearOnYear?: number
  yearToDate?: number
  today?: number
  unitMeasure?: string
}

const props = withDefaults(
  defineProps<{
    priceIndex: TPriceIndex
    priceSummary: TPriceSummaryItem[]
    methodologyFileInfo?: TDownloadFile | null
    category: string
    subscription?: string | null
    slug: string
    title?: string | null
    altTitle?: string | null
  }>(),
  {
    methodologyFileInfo: null,
    subscription: null,
    title: null,
    altTitle: null,
  },
)

const router = useRouter()
const route = useRoute()

const detailedData = ref<{
  [key: string]: Array<TGeneral>
}>({})
props.priceIndex.detailed.forEach((product) => {
  detailedData.value[product.product] = product.data
})

const summaryFormat = ref<string>('table')

const showIndex = computed(() => {
  return (
    Object.keys(detailedData.value).length > 0 &&
    Object.entries(detailedData.value).every(([_, values]) => values.length > 0)
  )
})

const handleFormatChange = (format: string) => {
  summaryFormat.value = format
  setUrlQueryParams(format)
}

watch(summaryFormat, (newVal) => {
  setUrlQueryParams(newVal)
})

onMounted(() => {
  summaryFormat.value = setDefaultQueryParams()
})

const setDefaultQueryParams = () => {
  const tabs = ['table', 'index']
  const defaultTab = tabs[0]

  if (route && route.query && route?.query?.tab) {
    const currentTab = route.query.tab as string
    if (tabs.includes(currentTab)) {
      return currentTab
    }
  }

  setUrlQueryParams(defaultTab)

  return defaultTab
}

const setUrlQueryParams = (tab: string) => {
  if (router && route) {
    router.replace({ query: { ...route.query, tab } })
  }
}
</script>
